import api from "../api";
import {appConfig} from "../app-config";

// Browser push (Web Push + our VAPID key). States follow the spec: unsupported | permission_required | enabled | blocked.
// The server keeps one subscription per browser; a user can have many (docs/tasks/12-notifications-plan.md).

const SW_URL = "/push-sw.js";
let registrationPromise = null;

export function pushSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window
        && window.isSecureContext;
}

// iOS only allows push for the installed web app (Add to Home Screen, iOS 16.4+)
export function needsInstallForPush() {
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent || "");
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    return ios && !standalone;
}

export function registerPushWorker() {
    if (!pushSupported()) return Promise.resolve(null);
    if (!registrationPromise) {
        const url = `${SW_URL}?api=${encodeURIComponent(appConfig.apiUrl)}`;
        registrationPromise = navigator.serviceWorker.register(url, {scope: "/"})
            .then(() => navigator.serviceWorker.ready)
            .catch((error) => {
                console.warn("[push] service worker registration failed", error);
                registrationPromise = null;
                return null;
            });
    }
    return registrationPromise;
}

export async function currentSubscription() {
    const registration = await registerPushWorker();
    return registration ? registration.pushManager.getSubscription() : null;
}

export async function pushState() {
    if (!pushSupported()) return needsInstallForPush() ? "install_required" : "unsupported";
    if (Notification.permission === "denied") return "blocked";
    if (Notification.permission !== "granted") return "permission_required";
    return (await currentSubscription()) ? "enabled" : "permission_required";
}

function keyBytes(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const raw = window.atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

function deviceLabel() {
    const agent = navigator.userAgent || "";
    const browser = /Edg\//.test(agent) ? "Edge" : /Firefox\//.test(agent) ? "Firefox" : /Chrome\//.test(agent) ? "Chrome" : /Safari\//.test(agent) ? "Safari" : "مرورگر";
    const system = /Windows/.test(agent) ? "Windows" : /Android/.test(agent) ? "Android" : /iPhone|iPad/.test(agent) ? "iOS" : /Mac OS/.test(agent) ? "macOS" : /Linux/.test(agent) ? "Linux" : "";
    return [browser, system].filter(Boolean).join(" · ");
}

async function saveSubscription(subscription) {
    const response = await api.post("/notifications/push-subscriptions", {
        data: {subscription: subscription.toJSON(), deviceLabel: deviceLabel()},
    });
    return response?.data?.data;
}

const ERROR_KEY = "zofino.push.error";

function rememberError(code) {
    try {
        if (code) window.localStorage.setItem(ERROR_KEY, JSON.stringify({code, at: Date.now()}));
        else window.localStorage.removeItem(ERROR_KEY);
    } catch (error) {
        // per-device convenience only
    }
}

// the last reason this browser could not register for push (shown in the settings diagnostics)
export function lastPushError() {
    try {
        return JSON.parse(window.localStorage.getItem(ERROR_KEY) || "null");
    } catch (error) {
        return null;
    }
}

const sameKey = (subscription, publicKey) => {
    const current = subscription?.options?.applicationServerKey;
    if (!current) return true;
    const bytes = new Uint8Array(current);
    const expected = keyBytes(publicKey);
    return bytes.length === expected.length && bytes.every((value, index) => value === expected[index]);
};

// asks for permission (must run from a click) and registers this browser
export async function enablePush(publicKey, {fresh = false} = {}) {
    if (!pushSupported()) throw new Error(needsInstallForPush() ? "push.install-required" : "push.unsupported");
    if (!publicKey) throw new Error("push.not-configured");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error(permission === "denied" ? "push.blocked" : "push.dismissed");
    const registration = await registerPushWorker();
    if (!registration) throw new Error("push.worker-failed");
    let subscription = await registration.pushManager.getSubscription();
    // a subscription made with another server key (or a stuck one) is replaced
    if (subscription && (fresh || !sameKey(subscription, publicKey))) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
    }
    if (!subscription) {
        try {
            subscription = await registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: keyBytes(publicKey)});
        } catch (error) {
            // the browser could not reach its own push service (Chrome → Google, Edge → Microsoft, Firefox → Mozilla)
            const code = error?.name === "NotAllowedError" ? "push.blocked" : "push.service-unreachable";
            rememberError(`${code}:${error?.name || "Error"}`);
            throw new Error(code);
        }
    }
    const saved = await saveSubscription(subscription);
    rememberError(null);
    return saved;
}

// sha-256 of this browser's endpoint, to match it with the server's device list without sending the token around
export async function currentEndpointHash() {
    const subscription = await currentSubscription().catch(() => null);
    if (!subscription || !window.crypto?.subtle) return null;
    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(subscription.endpoint));
    return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export async function currentPushService() {
    const subscription = await currentSubscription().catch(() => null);
    try {
        return subscription ? new URL(subscription.endpoint).host : null;
    } catch (error) {
        return null;
    }
}

export async function disablePush() {
    const subscription = await currentSubscription();
    if (!subscription) return null;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe().catch(() => {});
    const response = await api.post("/notifications/push-subscriptions/remove", {data: {endpoint}});
    return response?.data?.data;
}

// on app start: keep the server's copy of this browser's subscription current (rotation, account switch, a lost or
// outdated subscription). Permission is already granted, so this never shows a prompt.
export async function syncPushSubscription(publicKey) {
    try {
        if (!pushSupported() || Notification.permission !== "granted") return;
        if (publicKey) {
            await enablePush(publicKey);
            return;
        }
        const subscription = await currentSubscription();
        if (subscription) await saveSubscription(subscription);
    } catch (error) {
        console.warn("[push] subscription sync failed", error?.message || error);
    }
}

// before sign-out: this browser should stop receiving the signed-out user's notifications
export async function detachPushSubscription() {
    try {
        const subscription = await currentSubscription();
        if (!subscription) return;
        await api.post("/notifications/push-subscriptions/remove", {data: {endpoint: subscription.endpoint}}, {suppressGlobalErrorHandler: true});
    } catch (error) {
        // best effort
    }
}

// Open-tab fallback: when the server wanted a push right now and this tab is in the background, show the system
// notification from here. Same tag as the server push, so the browser never shows it twice.
export async function showLocalNotification(item) {
    try {
        if (!item?.delivery?.showNow || !pushSupported() || Notification.permission !== "granted") return;
        if (document.visibilityState === "visible" && document.hasFocus()) return;
        const registration = await registerPushWorker();
        if (!registration?.active) return;
        const key = item.documentId ?? item.id;
        const path = String(item.actionUrl || "/inbox?tab=notifications");
        const workspace = item.delivery?.push?.workspace || item.workspace?.documentId || null;
        const url = `/#${path.startsWith("/") ? path : `/${path}`}${path.includes("?") ? "&" : "?"}notification=${encodeURIComponent(key)}${workspace ? `&ws=${encodeURIComponent(workspace)}` : ""}`;
        registration.active.postMessage({
            type: "zofino:show-notification",
            // same text as the server push (composed once on the server); older rows fall back to title/message
            notification: {
                title: item.delivery?.push?.title || item.title,
                body: item.delivery?.push?.body || [item.message, item.contextLabel].filter(Boolean).join("\n").slice(0, 220),
                tag: `zofino-${key}`,
                url,
                requireInteraction: item.delivery?.priority === "critical",
            },
        });
    } catch (error) {
        // never break the realtime pipeline
    }
}

// the service worker asks an open tab to navigate (HashRouter: only the hash changes)
export function listenForPushNavigation(onSubscriptionChanged) {
    if (!pushSupported()) return () => {};
    const handler = (event) => {
        if (event.data?.type === "zofino:navigate" && event.data.url) {
            const url = new URL(event.data.url, window.location.origin);
            if (url.origin === window.location.origin) window.location.hash = url.hash.replace(/^#/, "");
        }
        if (event.data?.type === "zofino:push-subscription-changed") onSubscriptionChanged?.();
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
}
